'use client';

import Link from 'next/link';
import { usePathname, useRouter } from "next/navigation";
import { twMerge } from 'tailwind-merge';
import ThemedImage from '../ThemedImage';
import { FormEvent, HTMLAttributes, ReactNode, Suspense, forwardRef, useEffect, useRef, useState } from 'react';
import { ILocalizedParams } from '../../../models/ICommon';
import { useTheme } from 'next-themes';
import BurgerMenu from '../svg/BurgerMenu';
import Close from '../svg/Close';
import Sun from '../svg/Sun';
import Search from '../svg/Search';
import { IMenuItem } from '../../../models/IMenuItem';
// import { socialIcons, socialsTypeMap } from './svg/socials/SocialsIcons';
import Image, { ImageProps } from 'next/image';
import { IGeneralSettings } from '../../../models/IGeneralSettings';
import Clock from '../svg/Clock';
import Arrow from '../svg/Arrow';
import Microphone from '../svg/Microphone';
import Stars from '../svg/Stars';
import dynamic from 'next/dynamic';
import ThemeSwitcher from './ThemeSwitcher';
import PollIcon from '../svg/PollIcon';
import Logo from './Logo';
import LatestNewsIcon from '../svg/LatestNewsIcon';
import { appConfig } from '../../../config';
import Minus from '../svg/Minus';
import Plus from '../svg/Plus';


const EremSocials = dynamic(() => import('../EremSocials'), {loading: () => <div/>});


interface Props {
  locale: string;
  menu: IMenuItem[];
}

interface SearchingProps {
  input: string;
  isSearching: boolean;
  inMenu?: boolean;
}

export default function Navbar({ locale, menu, settings }: ILocalizedParams<{ menu: IMenuItem[]; settings: IGeneralSettings; }>) {
  const [searchingState, setSearchingState] = useState<SearchingProps>({isSearching: false, input: '', inMenu: false});
  return (
    <>
      <DesktopNavbar locale={locale} menu={menu} settings={settings} searching={{state: searchingState, setState: setSearchingState}}/>
      <MobileNavbar locale={locale} menu={menu} settings={settings} searching={{state: searchingState, setState: setSearchingState}}/>
      <Link  href={`/search?q=${searchingState.input}`} className='z-[-1000] hidden search-link'></Link>
    </>
  )
}

const isSelected = (href: string, pathname: string) => {
  const strippedPath = pathname.split('?')[0];
  strippedPath.replaceAll('/ar', '');
  if (href === '/') return strippedPath === '/';
  else return strippedPath.endsWith(href.startsWith('/') ? href : `/${href}`);
}


function Utils({ locale, className, searching, hideTheme }: ILocalizedParams<{ hideTheme?: boolean; className?: string, searching: {state: SearchingProps; setState: (set: (prevState: any) => any) => void}}>) {
  const pathname = usePathname();
  const isSearchPage = pathname.split('?')[0].endsWith('/search');

  const [show ,setShow] = useState(true);
  useEffect(() => {
    setShow(true);
  },[]);

  return (
    <div className={twMerge('flex items-center gap-x-[24px] [&>*]:cursor-pointer flex-shrink-0 justify-end', searching.state.isSearching && 'w-fit', className)}>
      {searching.state.isSearching ? 
      <Link hrefLang={locale} title={'ابحث'} href={`/search?q=${searching.state.input}`} className=' grid place-items-center' onClick={() => searching.setState(prevState => ({...prevState, isSearching: !prevState.isSearching, inMenu: false}))}>
      <Search className='w-[26px] h-[26px] tablet:w-[24px] tablet:h-[24px] desktop:w-[18px] desktop:h-[18px] [&>*]:fill-gray-700 dark:[&>*]:fill-gray-500' />
    </Link>
      :  (
        <>
         <Link href={'/latest-news'} className='hidden desktop:flex items-center h-[36.5px] gap-[6px] bg-secondary rounded-[6px] px-[14px] dark:bg-secondary'>
      <LatestNewsIcon className='!h-[24px] !w-[24px] fill-white mb-[3px]'/>
      <span className='text-white leading-none whitespace-nowrap'>آخر الأخبار</span>
      </Link>
         <Link href={'/voting'} className='hidden desktop:flex items-center h-[36.5px] gap-[6px] bg-black rounded-[6px] px-[14px] dark:bg-quaternary'>
      <PollIcon className='[&>*]:stroke-white'/>
      <span className='text-white leading-none whitespace-nowrap'>استطلاعات</span>
      </Link>
        {show ? <ThemeSwitcher className={twMerge(hideTheme && 'hidden desktop:block')}/> : <div className='w-[20px]'/>}
      <div className=' grid place-items-center' onClick={() => isSearchPage ? undefined : searching.setState(prevState => ({...prevState, isSearching: true}))}>
        <Search className='w-[26px] h-[26px] tablet:w-[24px] tablet:h-[24px] desktop:w-[18px] desktop:h-[18px]' />
      </div>
     
        </>
      )}
    </div>
  )
}


function DesktopNavbar({ 
  locale = 'ar', 
  menu, 
  settings, 
  searching,
}: ILocalizedParams<{ 
  menu: IMenuItem[]; 
  settings: IGeneralSettings; 
  searching: {state: SearchingProps; setState: (set: (prevState: any) => any) => void};
}
  >) {

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searching.state.input) return;
    searching.setState(() => ({input: '', isSearching: false, inMenu: false}));
    const elem = document.querySelector<HTMLElement>('.search-link-desktop')!;
    elem.click();
  }

  const ref = useRef<any>();

  const escHandler = (event: KeyboardEvent) => {
    const {key} = event;
      if (key === "Escape") searching.setState(() => ({isSearching: false, input: '', inMenu: false}));
  }

  const clickHandler = (event: MouseEvent) => {
    const {target} = event;
      if (ref.current && !ref.current.contains(target)) searching.setState(() => ({isSearching: false, input: '', inMenu: false}));
  }

  useEffect(() => {
    document.addEventListener('keydown', escHandler);
    document.addEventListener('mousedown', clickHandler);
    return () => {
      document.removeEventListener('keydown', escHandler);
      document.removeEventListener('mousedown', clickHandler);
    }
  },[]);

  const now = new Date();
  return (
    <>
    <div className='w-full bg-white dark:bg-tertiary sticky top-0 left-0 z-[15]'>


       <div className='w-full max-w-[1296px] mx-auto hidden desktop:flex flex-col h-[138px] border-b-[1px] border-b-gray-0 dark:border-b-lightBorder desktopSm:px-[24px] desktopSm:border-b-0'>
          <div className='w-full flex items-center [&>*]:flex-1 justify-between gap-x-[16px] border-b border-b-gray-0 dark:border-b-lightBorder h-[90px]'>
          {searching.state.isSearching  ? (
            <form className='flex items-center bg-transparent w-full h-full gap-x-[10px]' onSubmit={onSubmit}>
            <Close onClick={() => searching.setState(prevState => ({...prevState, isSearching: false, inMenu: false}))} className='flex-shrink-0 h-[18px] w-[18px] cursor-pointer dark:[&>*]:fill-white'/>
            <input
            value={searching.state.input}
            onChange={({target: {value}}) => searching.setState(prevState => ({...prevState, input: value}))}
            autoFocus
            placeholder='ابحث هنا'
            className='h-full w-full  bg-transparent border-none outline-none'
            />
            <input className='hidden z-[-100]' type='submit' />
            <Link href={`/search?q=${searching.state.input}`} className='hidden z-[-100] search-link-desktop'></Link>
            </form>
        ):  (
            <>
            <EremSocials settings={settings} expandable={false}/>
            <Logo locale={locale} settings={settings} />
            <Utils locale='ar' searching={searching} />

            </>
        )}

      </div>
      <div className='w-full flex items-center justify-center gap-[48px] h-[48px]'>
        <DesktopMenu locale={locale} menu={menu} settings={settings}/>

      </div>
        </div>
            </div>
        </>
  )
}


function MobileNavbar({ locale, menu, settings, searching }: ILocalizedParams<{ menu: IMenuItem[]; settings: IGeneralSettings; searching: {state: SearchingProps; setState: (set: (prevState: any) => any) => void} }>) {

  const router = useRouter();
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searching.state.input) return;
    searching.setState(() => ({input: '', isSearching: false, inMenu: false}));
    const elem = document.querySelector<HTMLElement>('.search-link-mobile')!;
    elem.click();
  }

  const ref = useRef<any>();

  const escHandler = (event: KeyboardEvent) => {
    const {key} = event;
      if (key === "Escape") searching.setState(() => ({isSearching: false, input: '', inMenu: false}));
  }

  const clickHandler = (event: MouseEvent) => {
    const {target} = event;
      if (ref.current && !ref.current.contains(target)) searching.setState(() => ({isSearching: false, input: '', inMenu: false}));
  }

  useEffect(() => {
    document.addEventListener('keydown', escHandler);
    document.addEventListener('mousedown', clickHandler);
    return () => {
      document.removeEventListener('keydown', escHandler);
      document.removeEventListener('mousedown', clickHandler);
    }
  },[]);
  return (
    <>
    <div className='sticky top-0 left-0 z-[1000] w-full flex-shrink-0 flex desktop:hidden items-center justify-between bg-white dark:bg-tertiary border-y-solid border-y-[1px] border-y-gray-0 dark:border-y-lightBorder h-[80px] tablet:h-[106px] px-[24px] border-b-[1px] border-b-gray-0 dark:border-b-lightBorder'>
      {searching.state.isSearching && !searching.state.inMenu ? (
        <form className='flex items-center bg-transparent gap-x-[8px] w-full' onSubmit={onSubmit}>
          <Close onClick={() => searching.setState(prevState => ({...prevState, isSearching: false, inMenu: false}))} className='flex-shrink-0 h-[22px] w-[22px] dark:[&>*]:fill-white'/>
          <input
          value={searching.state.input}
          onChange={({target: {value}}) => searching.setState(prevState => ({...prevState, input: value}))}
          autoFocus
          placeholder='ابحث هنا'
          className='h-full w-full bg-transparent border-none outline-none h5'
          />
          <input className='hidden z-[-100]' type='submit'  />
          <Link href={`/search?q=${searching.state.input}`} className='hidden z-[-100] search-link-mobile'></Link>
        </form>
      ):  (
        <div className='w-full flex items-center justify-between [&>*]:flex-1 h-full'>
          <MobileMenu searching={searching} locale={locale} menu={menu} settings={settings} />
        <Logo locale={locale} settings={settings} />
          <Utils locale={locale} searching={searching} hideTheme />
        </div>
        )}
    </div>
    <div className='flex items-center justify-center gap-[16px] tablet:hidden w-full px-[16px] py-[12px]'>
      <Link href={'/latest-news'} className='flex items-center justify-center gap-[8px] h-[52px] flex-1 max-w-[190px] shrink-0 bg-secondary rounded-[6px] dark:bg-secondary'>
<LatestNewsIcon className='!h-[24px] !w-[24px] fill-white mb-[3px]'/>
      <span className='text-white leading-none h6'>آخر الأخبار</span>
      </Link>
                    <Link href={'/voting'} className='flex items-center justify-center gap-[8px] h-[52px] flex-1 max-w-[190px] shrink-0 bg-black rounded-[6px] dark:bg-quaternary'>
      <PollIcon className='[&>*]:stroke-white !h-[24px]'/>
      <span className='text-white leading-none h6'>استطلاعات</span>
      </Link>
    </div>
        </>
  )

}

function MobileMenu({ menu, settings, searching }: Props & {searching: {state: SearchingProps; setState: (set: (prevState: any) => any) => void}; settings: IGeneralSettings;}) {

  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    const newState = !open;
    document.documentElement.style.overflowY = newState ? 'unset' : 'hidden';
    setOpen(newState);
  }

  const pathname = usePathname();
  
  
  const menuRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    if (open && !menuRef.current?.contains(e.target)) toggleOpen();
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClick, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.documentElement.style.overflowY = 'unset';
    };
  });


  return (
    <>
      <Menu settings={settings} toggleOpen={toggleOpen} ref={menuRef} menu={menu} pathname={pathname} open={open} searching={searching}/> 

            {/* <div
        className={twMerge('fixed -top-[150vh] -right-[150vh] w-[300vh] h-[300vh] bg-gray-100 dark:bg-quaternary rounded-full z-[1100] transition-transform duration-700', open ? "scale-100 ease-[cubic-bezier(.55,0,.1,1)]" : "scale-0 ease-in")}
      /> */}

      <div className='flex-1'>

      <div
        className={twMerge('cursor-pointer grid place-items-end-center items-center h-fit w-fit')}
        onClick={() => toggleOpen()}
        >
        {open ? <Close className='h-[20px] w-[20px] mr-[5px] dark:[&>*]:fill-white' /> : <BurgerMenu className='h-[19px] w-[26px] dark:[&>*]:stroke-white' />}
      </div>
        </div>
    </>
  )
}


interface IMenuProps {
  pathname: string; 
  menu: IMenuItem[]; 
  open: boolean;
  toggleOpen: Function;
  searching: {state: SearchingProps; setState: (set: (prevState: any) => any) => void}
}

const Menu = forwardRef(({
  menu, 
  pathname,
  open,
  toggleOpen,
  settings,
  searching
  }:  IMenuProps & {settings: IGeneralSettings;}, menuRef: any) => {


  const [expanded, setExpanded] = useState(() => menu.map(() => false));

  const { push } = useRouter();
  const handleLink = (href: string) => {
    push(href);
    toggleOpen();
  }

    const isSearchPage = pathname.split('?')[0].endsWith('/search');



    const [input, setInput] = useState('');

    const handleSearch = () => {
      if (!input) return;
      toggleOpen();
      push(`/search?q=${input}`);
      setInput('');
    }

    
      const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searching.state.input) return;
    searching.setState(() => ({input: '', isSearching: false, inMenu: false}));
    const elem = document.querySelector<HTMLElement>('.search-link-mobile')!;
    elem.click();
    toggleOpen();
  }


  // const handleClick = (e: any) => {
  //   if (!e.target.contains(menuRef.current)) toggleOpen();
  // }

  // useEffect(() => {
  //   window.addEventListener('mousedown', handleClick);
  //   return () => {
  //     window.removeEventListener('mousedown', handleClick);
  //   }
  // }, []);
  return (
    <div className={twMerge('bg-black bg-opacity-70 dark:bg-whit dark:bg-opacity-40 backdrop-blur-sm fixed right-[-100vw] top-0 h-[100vh] w-[100vw] z-[1200] overflow-y-hidden', open && 'right-0')}>
  <div
    ref={menuRef}
    className={twMerge('bg-white dark:bg-tertiary flex flex-col justify-between h-full border-solid border-b-secondary-400 w-[calc(100vw-50px)] py-[12px] translate-x-[100vw] transition-all duration-700', open && "translate-x-0")}
    >
      
         <div className='w-full flex items-center justify-between h-[80px] px-[24px] mb-[24px]'>
      
          
          

          <div
            className={twMerge('cursor-pointer grid place-items-end items-center h-fit w-fit')}
            onClick={() => toggleOpen()}
            >
          <Close className='h-[20px] w-[20px] dark:[&>*]:fill-white' /> 
          </div>
        <Logo locale={'ar'} settings={settings} wrapperClassName='place-items-start' />
        <div/>
          {/* <div className='flex items-center justify-end' onClick={() => isSearchPage ? undefined : searching.setState(prevState => ({...prevState, isSearching: !prevState.isSearching, inMenu: !prevState.inMenu}))}>
        <Search className='w-[26px] h-[26px] tablet:w-[24px] tablet:h-[24px] desktop:w-[18px] desktop:h-[18px]' />
      </div> */}



        </div>
       
        <div className='flex flex-col h-full overflow-y-auto px-[24px] gap-y-[16px] pb-[24px] mb-[24px]'>
          <div className='items-center justify-center gap-[12px] hidden tablet:flex'>

                    <Link href={'/latest-news'} onClick={() => toggleOpen()} className='flex items-center h-[36.5px] shrink-0 w-fit gap-[6px] bg-secondary rounded-[6px] px-[14px] dark:bg-secondary'>
<LatestNewsIcon className='!h-[24px] !w-[24px] fill-white mb-[3px]'/>
      <span className='text-white leading-none'>آخر الأخبار</span>
      </Link>
                    <Link href={'/voting'} onClick={() => toggleOpen()} className='flex items-center h-[36.5px] shrink-0 w-fit gap-[6px] bg-black rounded-[6px] px-[14px] dark:bg-quaternary'>
      <PollIcon className='[&>*]:stroke-white'/>
      <span className='text-white leading-none'>استطلاعات</span>
      </Link>
          </div>
             <div className='h-[60px] w-full border-gray-0 dark:border-lightBorder bg-gray-0 dark:bg-quinary border-[1.5px] p-[16px] items-center justify-between gap-x-[24px] rounded-[6px] hidden tablet:flex'>
                  <input 
                 suppressContentEditableWarning
                 autoComplete='off'
                 className='outline-none border-none bg-transparent w-full h-full text-black dark:text-white h5'
                placeholder='ابحث هنا'
                 value={input}
                 onChange={({ target: { value } }) => setInput(value)}
                 type='text'
                    />
                    <button className='w-fit h-full grid place-items-center' onClick={() => handleSearch()}>
                  <Search className='aspect-square w-[24px] flex-shrink-0'/>
                    </button>
                </div>
               <div
        className={twMerge('cursor-pointer flex items-center justify-end h-fit w-full')}
        >



            <div className='flex items-center justify-between gap-[24px] w-full border-b border-b-gray-0 dark:border-b-lightBorder pb-[16px]'>
              <Link 
                hrefLang={'ar'} 
                target={'_self'}
                onClick={() => toggleOpen()}
                href={'/'} 
                className={twMerge(`cursor-pointer flex bg-transparent w-fit whitespace-nowrap w-fullflex items-center justify-between`)}
                >
                <span className='font-medium text-[22px]'>الرئيسية</span>
              </Link>
          <ThemeSwitcher />
            </div>

          </div>
        {menu.map((x, i) => {
          const hasChildren = x.children?.length;
          const isExpanded = hasChildren && Boolean(expanded[i]);
           const isLatestNews = x.destination.endsWith('latest-news');
          return (
            <div className={twMerge('flex flex-col border-b border-b-gray-0 dark:border-b-lightBorder', i === 0 && '[&>*]:border-t-0')} key={i + 'container'}>
                <div className='flex items-center justify-between cursor-pointer pb-[16px]'   >
                  <Link 
                    hrefLang={'ar'} 
                    target={x.newTab ? '_blank' : '_self'}
                    title={x.title!}
                    key={i + 'link'}
                    onClick={() => handleLink(`${x.targetType === 3 ? '' : '/'}${x.destination}`)}
                    href={`${x.targetType === 3 ? '' : '/'}${x.destination}`} 
                    className={twMerge(`cursor-pointer flex bg-transparent border-b-[1px] w-fit border-b-gray-0 dark:border-b-lightBorder last:border-b-0 whitespace-nowrap w-fullflex items-center justify-between`, isSelected(x.destination, pathname) && 'relative [&>*]:text-primary dark:[&>*]:text-primary before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[3px] before:bg-secondary', hasChildren && 'border-b-0')}
                    >
                  <span className='font-medium text-[22px]'>{x.title}</span>
                </Link>
                {hasChildren ? 
                    (
                      <div 
                      className={twMerge('grid place-items-center cursor-pointer',)}
                      onClick={() => setExpanded(prevState => prevState.map((x,j) => i === j ? !x : x))}
                      >

                      {isExpanded ? <Minus className='[&>*]:fill-dark dark:[&>*]:fill-white'/> : <Plus className='[&>*]:[&>*]:fill-dark dark:[&>*]:[&>*]:fill-white'/>}
                      </div>
                    )
                       : null}
                  </div>

          {isExpanded ? (
            <div className='flex flex-col py-[6px] pr-[12px]'>
            {x.children!.map(c => (
              <Link 
              {...{
                title: c.title!,
                target: c.newTab ? '_blank' : '_self',
                onClick: () => handleLink(`${x.targetType === 3 ? '' : '/'}${x.destination}`),
                ...(c.newTab ? {rel: 'noopener'} : {}),
                href:`${c.targetType === 3 ? '' : '/'}${c.destination}`,
                className: twMerge('py-[8px] w-fit', isSelected(c.destination, pathname) && 'relative before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[3px] before:bg-secondary')
              }}
                key={c.id}
              >
                <span className={twMerge('lg', isSelected(c.destination, pathname) && '!text-primary')}>{c.title}</span>
              </Link>
            ))}
            </div>
            ) : null}
          </div>
        )
      })} 
      </div>

      <div className='flex flex-col gap-[24px]'>
  

      {/* <div className='flex items-center justify-center w-full gap-[24px] tablet:hidden'>
        <span className='h6'>انقر لتغيير النمط</span>
        <ThemeSwitcher className='p-[6px] bg-black dark:bg-tertiary [&>*]:[&>*]:fill-white [&>*]:h-[20px] [&>*]:w-[20px] rounded-[4px] grid place-items-center'/>
        </div> */}
      <div className='flex flex-col items-center justify-center gap-[24px] tablet:border-y tablet:border-y-gray-0 tablet:py-[48px] tablet:dark:border-y-lightBorder'>
        <span className='hidden tablet:block h4'>تابعونا على مواقع التواصل الاجتماعي</span>
        <EremSocials expandable={false} settings={settings} className='flex p-[24px] justify-center items-center border-y border-y-gray-0 dark:border-y-lightBorder tablet:border-y-0 gap-x-[24px] w-full tablet:py-0'/>
      </div>
        <span className='px-[24px] text-center w-full' dangerouslySetInnerHTML={{__html: settings.publisherSettings.copyright}}/>
      </div>
  </div>
  </div>
  )
});



function DesktopMenu({ menu, locale }: Props & {settings: IGeneralSettings;}) {
  const pathname = usePathname();
  // const moreItems = menu.filter(x => x.positions.includes(3));


  return (
    <div className='flex items-center h-full gap-x-[36px] desktop:max-w-[1296px] overflow-visible relative desktopSm:border-b desktopSm:border-b-gray-0 desktopSm:dark:border-b-lightBorder desktopSm:w-full desktopSm:justify-center'>
      {menu?.filter(x => x.positions.includes(1)).sort((a, b) => a.weight - b.weight).map(({ id, destination, targetType, title, children, newTab }, i) => {
        const hasChildren = children?.length;
        const isLatestNews = destination.endsWith('latest-news');
        return (
          <div
            className={twMerge('grid place-items-center relative h-full whitespace-nowrap group overflow-visible')}
            key={id + title!}
            >
            <Link
              {...{
                title: title!,
                target: newTab ? '_blank' : '_self',
                ...(newTab ? {rel: 'noopener'} : {}),
                href:`${targetType === 3 ? '' : '/'}${destination}`,
                className: twMerge('h-full min-w-full flex items-center justify-center gap-x-[8px] dark:group-hover:[&>*:first-child]:text-primary', isSelected(destination, pathname) && 'relative before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[2px] before:bg-secondary dark:before:bg-light group')
              }}
              >
              <span className={twMerge('md font-medium text-dark dark:text-light', isSelected(destination, pathname) && 'text-primary dark:text-primary')}>{title}</span>
              {hasChildren ? <Arrow className='h-[10px] w-[10px] [&>*]:fill-dark dark:[&>*]:fill-white group-hover:rotate-[180deg] transition-all duration-300'/> : null}

            </Link>
            {hasChildren ?
              <div className={twMerge(`z-[15] w-fit bg-white absolute top-full right-0 hidden h-fit group-hover:flex flex-col border-[1px] dark:bg-tertiary rounded-[8px] shadow-menu min-w-[175px] border-gray-0 dark:border-lightBorder p-[8px]`)}>
                {children.map((c, i) => { 
                  return (
                    <Link
                      {...{
                        hrefLang: locale,
                        title: c.title!,
                        target: c.newTab ? '_blank' : '_self',
                        ...(c.newTab ? {rel: 'noopener'} : {}),
                        href:`${c.targetType === 3 ? '' : '/'}${c.destination}`,
                        className: twMerge(`px-[16px] rounded-[6px] cursor-pointer  bg-transparent pb-[8px] pt-[8px] border-b-[1px] border-b-gray-0 dark:border-b-lightBorder last:border-b-0 whitespace-nowrap flex items-center w-full sm text-dark dark:text-light hover:bg-gray-0 hover:!text-primary dark:hover:!text-white dark:hover:bg-primary`, isSelected(c.destination, pathname) && 'text-primary dark:text-primary' )
                      }}
                      key={c.id}
                    >
                      {c.title}
                    </Link>
                  )
                })}
              </div> : null}
          </div>
        )
      })}
          {/* {moreItems.length ?
        <div 
          onClick={() => {}}
          className={twMerge(`flex items-center gap-x-[8px] cursor-pointer group h-full relative`)}
        >
          <span className='h5'>المزيد</span>
          <Arrow className='group-hover:rotate-[180deg] transition-all duration-300 [&>*]:fill-dark dark:[&>*]:fill-white'/>

          <div className={twMerge(`z-[15] w-fit bg-white dark:bg-gray-800 absolute top-full right-0 hidden h-fit group-hover:flex flex-col border-[1px] border-gray-300 dark:border-gray-700 shadow-menu min-w-[175px]`)}>
                {moreItems.map((x, i) => { 
                  return (
                    <Link 
                    {...{
                      hrefLang: locale,
                      title: x.title!,
                      key: x.id,
                      target: x.newTab ? '_blank' : '_self',
                      ...(x.newTab ? {rel: 'noopener'} : {}),
                      href:`${x.targetType === 3 ? '' : '/'}${x.destination}`,
                      className: twMerge(`px-[16px] cursor-pointer  bg-transparent pb-[8px] pt-[8px] border-b-[1px] border-b-gray-300 dark:border-b-gray-700 dark:bg-gray-825 last:border-b-0 whitespace-nowrap flex items-center w-full md hover:underline`, isSelected(x.destination, pathname) && 'relative before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[3px] before:bg-secondary')
                    }}
                    >
                      {x.title}
                    </Link>
                  )
                })}
              </div> 
      </div>
              : null} */}

    </div>
  )
}






function AdditionalPages({close, className, showBreakingNews}: {close?: Function, showBreakingNews: boolean; className?: string}) {
  const links: any[] = [
  ];


  const handleClick = () => {
    if (close) close();
  }


  return (
    <div className={twMerge('flex gap-[8px] tablet:gap-[12px] px-[24px] pb-[16px] desktop:pb-0 mb-[16px] desktop:mb-0 border-b-[1px] border-b-gray-0 dark:border-b-lightBorder desktop:border-b-0 desktop:px-0', className)}>    
      {
        links.map(l => (
          <a 
          onClick={() => handleClick()}
          href={l.href}
          hrefLang={'ar'}
          key={l.title}
          title={l.title}
          className={twMerge('w-full flex items-center justify-center gap-x-[8px] px-[16px] rounded-[4.8px] h-[39px]',l.className)}>
              {l.icon}
              <h6 className={twMerge('text-white whitespace-nowrap')}>{l.title}</h6>
          </a>
        ))
      }
    </div>
  )


}